import React, { useState, useRef } from 'react';
import { 
  exportToJSON, 
  importFromJSON, 
  validateJSONData, 
  getCategoryFilename, 
  getCategoryDisplayName 
} from '../utils/jsonUtils.js';
import './JsonImportExport.css';

const JsonImportExport = ({ category, data, onDataUpdate, isEditing = false }) => {
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const fileInputRef = useRef(null);

  const handleExport = () => {
    if (!isEditing) {
      setMessage('Please enable edit mode to export data');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    const filename = getCategoryFilename(category);
    const result = exportToJSON(data, filename);
    
    setMessage(result.message);
    setMessageType(result.success ? 'success' : 'error');
    
    // Clear message after 3 seconds
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!isEditing) {
      setMessage('Please enable edit mode to import data');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Check file type
    if (!file.name.endsWith('.json')) {
      setMessage('Please select a JSON file');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    const result = await importFromJSON(file);
    
    if (!result.success) {
      setMessage(result.message);
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    // Validate the imported data
    const validation = validateJSONData(result.data, category);
    
    if (!validation.valid) {
      setMessage(validation.message);
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    // Update the data
    onDataUpdate(result.data);
    
    setMessage(`Successfully imported ${getCategoryDisplayName(category)} data`);
    setMessageType('success');
    
    // Clear message after 3 seconds
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);

    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportClick = () => {
    if (!isEditing) {
      setMessage('Please enable edit mode to import data');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }
    fileInputRef.current?.click();
  };

  return (
    <div className="json-import-export">
      <div className="json-controls">
        <button 
          onClick={handleExport}
          className={`json-button export-button ${!isEditing ? 'disabled' : ''}`}
          title={isEditing ? `Export ${getCategoryDisplayName(category)} to JSON` : 'Enable edit mode to export data'}
          disabled={!isEditing}
        >
          <i className="fas fa-download"></i>
          Export JSON
        </button>
        
        <button 
          onClick={handleImportClick}
          className={`json-button import-button ${!isEditing ? 'disabled' : ''}`}
          title={isEditing ? `Import ${getCategoryDisplayName(category)} from JSON` : 'Enable edit mode to import data'}
          disabled={!isEditing}
        >
          <i className="fas fa-upload"></i>
          Import JSON
        </button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleImport}
          style={{ display: 'none' }}
        />
      </div>
      
      {message && (
        <div className={`json-message ${messageType}`}>
          <i className={`fas ${messageType === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
          {message}
        </div>
      )}
      
      <div className="json-info">
        <details>
          <summary>JSON Import/Export Help</summary>
          <div className="json-help-content">
            <h4>Export:</h4>
            <p>Downloads the current {getCategoryDisplayName(category).toLowerCase()} data as a JSON file. <strong>Requires edit mode.</strong></p>
            
            <h4>Import:</h4>
            <p>Upload a JSON file to replace the current {getCategoryDisplayName(category).toLowerCase()} data. <strong>Requires edit mode.</strong></p>
            
            <h4>File Format:</h4>
            <p>The JSON file should contain the {getCategoryDisplayName(category).toLowerCase()} data in the correct format.</p>
            
            <h4>Backup:</h4>
            <p>Always export your data before importing new data to avoid data loss.</p>
          </div>
        </details>
      </div>
    </div>
  );
};

export default JsonImportExport;
